import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputPhotos from '@components/InputPhotos';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_BRAND, UPDATE_BRAND } from '@gql/brand';
import { toUploadFileInput, toUploadFilesInput } from '@modules/brands/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BrandItem, UploadFileValue } from '../types';

type BrandFormValues = {
    brandBanner: UploadFileValue;
    brandLogo: UploadFileValue;
    brandVideos: Array<File | { _id?: string; url?: string; name?: string; size?: number; mimetype?: string }>;
    brandPhotos: Array<File | { _id?: string; url?: string; name?: string; size?: number; mimetype?: string }>;
};

type BrandPhotosCardProps = {
    brand: BrandItem;
    onSuccess?: (data?: any) => void;
    onError?: (error?: any) => void;
    disabled?: boolean;
};

const BrandPhotosCard = ({ brand, onSuccess, onError, disabled }: BrandPhotosCardProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const defaultValues = useMemo<BrandFormValues>(
        () => ({
            brandBanner: (brand?.brandBanner as UploadFileValue) ?? null,
            brandLogo: (brand?.brandLogo as UploadFileValue) ?? null,
            brandVideos: (brand?.brandVideos as BrandFormValues['brandVideos']) ?? [],
            brandPhotos: (brand?.brandPhotos as BrandFormValues['brandPhotos']) ?? [],
        }),
        [brand],
    );

    const { control, handleSubmit, reset } = useForm<BrandFormValues>({
        mode: 'onChange',
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const [updateBrand, { loading }] = useMutation(UPDATE_BRAND, {
        onCompleted: data => {
            enqueueSnackbar('Photos de la marque modifiées avec succès', {
                variant: 'success',
            });
            onSuccess?.(data?.updateBrand);
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });
            onError?.(error);
        },
    });

    const submitHandler = async (values: BrandFormValues) => {
        if (!brand?._id) return;

        await updateBrand({
            variables: {
                id: brand._id,
                brandInput: {
                    brandName: brand?.brandName,
                    description: brand?.description,
                    sector: brand?.sector?._id,
                    brandLogo: toUploadFileInput(values.brandLogo),
                    brandBanner: toUploadFileInput(values.brandBanner),
                    brandPhotos: toUploadFilesInput(values.brandPhotos),
                    brandVideos: toUploadFilesInput(values.brandVideos),
                },
            },
            context: {
                fetchOptions: {
                    useUpload: true,
                },
            },
            refetchQueries: [
                {
                    query: GET_BRAND,
                    variables: { id: brand._id },
                },
            ],
            awaitRefetchQueries: true,
        });

        setFormState('view');
    };

    return (
        <FormContainer
            onBack={() => {}}
            formTitle="Nos Galeries"
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={disabled}
            onCancel={() => {
                reset(defaultValues);
            }}
            breadcrumbs={['Informations marque', 'Nos Galeries']}
            onSubmit={handleSubmit(submitHandler)}
        >
            <InputPhotos
                name="brandPhotos"
                label="Photos de la marque"
                control={control}
                viewOnly={formState === 'view'}
                helpText="Formats acceptés: JPG, PNG (Max: 5MB par image, 10 photos max)"
                validation={{
                    maxSizeInMB: 5,
                    maxCount: 10,
                    aspectRatio: 1,
                }}
                existingFiles={brand?.brandPhotos}
            />
        </FormContainer>
    );
};

export default BrandPhotosCard;
